<script setup lang="ts">
/**
 * A record as a chip — customer, contact, product, project: avatar (or an
 * icon in `#icon`) and name. Activating it opens a small card with a larger
 * avatar, the name, a subtitle, detail lines and the caller's `#actions`
 * (open, copy, write a mail — the domain actions stay with the module; the
 * slot gets `close()`).
 *
 * `openOn="click"` (default): left click, right click, Enter or Space open
 * the card, and the click does not reach a surrounding row. `openOn=
 * "contextmenu"`: only right click (and Enter/Space) open it, a left click
 * bubbles on — for table cells whose row opens on click. Double click emits
 * `activate` in both modes (e.g. open the record).
 *
 * With `href` or `to` the chip is a plain link instead, without a card.
 * An empty `label` renders `emptyText` („—“).
 */
import { computed, nextTick, ref, useId, watch } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import NxAvatar, { type NxAvatarShape, type NxAvatarStatus } from '../Avatar/NxAvatar.vue';
import type { NxHue } from '../lib/hue';
import type { NxAccentTone } from '../Panel/accent';
import NxPopover from '../Popover/NxPopover.vue';
import type { NxPopoverSide } from '../Popover/NxPopover.vue';
import type { NxEntityChipDetail } from './types';

const props = withDefaults(
  defineProps<{
    label: string;
    /** Second line of the card („Kunde · #1042“, an e-mail address). */
    subtitle?: string;
    /** Label/value lines of the card. */
    details?: readonly NxEntityChipDetail[];
    /** A small tag in the card head („Kein Konto“, „Gesperrt“). */
    badge?: string;
    /** Photo URL of the avatar. */
    src?: string;
    initials?: string;
    hue?: NxHue | number;
    tone?: NxAccentTone;
    shape?: NxAvatarShape;
    status?: NxAvatarStatus;
    /** Logical px of the chip avatar. */
    size?: number;
    /** Write the name next to the avatar. */
    showName?: boolean;
    openOn?: 'click' | 'contextmenu';
    /** Plain link instead of a card. */
    href?: string;
    /** Router link instead of a card. */
    to?: RouteLocationRaw;
    /** Controlled card state (`v-model:open`). */
    open?: boolean;
    side?: NxPopoverSide;
    /** Shown for an empty label. */
    emptyText?: string;
  }>(),
  {
    subtitle: undefined,
    details: () => [],
    badge: undefined,
    src: undefined,
    initials: undefined,
    hue: undefined,
    tone: undefined,
    shape: 'circle',
    status: undefined,
    size: 22,
    showName: true,
    openOn: 'click',
    href: undefined,
    to: undefined,
    open: undefined,
    side: 'bottom-start',
    emptyText: '—',
  }
);

const emit = defineEmits<{
  'update:open': [open: boolean];
  /** Double click on the chip. */
  activate: [event: MouseEvent];
}>();

const slots = defineSlots<{
  /** Replaces the avatar in chip and card; gets the size in logical px. */
  icon?(props: { size: number }): unknown;
  /** Extra card content under the details. */
  card?(): unknown;
  /** Card footer buttons; gets `close()`. */
  actions?(props: { close: () => void }): unknown;
}>();

const nameId = useId();
const card = ref<HTMLElement | null>(null);

const name = computed(() => props.label?.trim() ?? '');
const isLink = computed(() => !!(props.href || props.to));
const title = computed(() => [name.value, props.subtitle].filter(Boolean).join(' · '));

const localOpen = ref(props.open ?? false);
watch(
  () => props.open,
  (v) => {
    if (v !== undefined) localOpen.value = v;
  }
);
const isOpen = computed(() => props.open ?? localOpen.value);

function setOpen(next: boolean) {
  if (next === isOpen.value) return;
  if (props.open === undefined) localOpen.value = next;
  emit('update:open', next);
}
function close() {
  setOpen(false);
}

/** The popover toggles on every click; in contextmenu mode a click may only close. */
function onPopoverUpdate(next: boolean) {
  if (next && props.openOn === 'contextmenu') return;
  setOpen(next);
}

function onContextMenu(e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
  setOpen(true);
}

function onKeydown(e: KeyboardEvent) {
  // In click mode Enter/Space become a click the popover already handles.
  if (props.openOn !== 'contextmenu') return;
  if (e.key !== 'Enter' && e.key !== ' ') return;
  e.preventDefault();
  setOpen(!isOpen.value);
}

/**
 * Clicks inside the card never reach a surrounding row; clicks on the chip
 * only in click mode.
 */
function onRootClick(e: MouseEvent) {
  const inCard = !!card.value && card.value.contains(e.target as Node);
  if (inCard || props.openOn === 'click') e.stopPropagation();
}

watch(isOpen, (open) => {
  if (open) void nextTick(() => card.value?.focus({ preventScroll: true }));
});

defineExpose({ open: () => setOpen(true), close });
</script>

<template>
  <span v-if="!name" class="nx-entity-chip is-empty">{{ emptyText }}</span>

  <component
    :is="to ? RouterLink : 'a'"
    v-else-if="isLink"
    class="nx-entity-chip nx-entity-chip__trigger is-link"
    v-bind="to ? { to } : { href }"
    :title="title"
    :aria-label="showName ? undefined : name"
    data-testid="nx-entity-chip"
    @dblclick="emit('activate', $event)"
  >
    <span class="nx-entity-chip__media">
      <slot name="icon" :size="size">
        <NxAvatar
          :name="name"
          :src="src"
          :initials="initials"
          :hue="hue"
          :tone="tone"
          :shape="shape"
          :status="status"
          :size="size"
          decorative
        />
      </slot>
    </span>
    <span v-if="showName" class="nx-entity-chip__name">{{ name }}</span>
  </component>

  <span
    v-else
    class="nx-entity-chip"
    :class="{ 'is-open': isOpen }"
    data-testid="nx-entity-chip"
    @click="onRootClick"
  >
    <NxPopover :open="isOpen" :side="side" :offset="6" lazy @update:open="onPopoverUpdate">
      <template #trigger>
        <button
          type="button"
          class="nx-entity-chip__trigger"
          :title="title"
          :aria-label="showName ? undefined : name"
          aria-haspopup="dialog"
          :aria-expanded="isOpen"
          @contextmenu="onContextMenu"
          @dblclick="emit('activate', $event)"
          @keydown="onKeydown"
        >
          <span class="nx-entity-chip__media">
            <slot name="icon" :size="size">
              <NxAvatar
                :name="name"
                :src="src"
                :initials="initials"
                :hue="hue"
                :tone="tone"
                :shape="shape"
                :status="status"
                :size="size"
                decorative
              />
            </slot>
          </span>
          <span v-if="showName" class="nx-entity-chip__name">{{ name }}</span>
        </button>
      </template>

      <div
        ref="card"
        class="nx-entity-card"
        tabindex="-1"
        :aria-labelledby="nameId"
        data-testid="nx-entity-card"
      >
        <div class="nx-entity-card__head">
          <span class="nx-entity-card__media">
            <slot name="icon" :size="40">
              <NxAvatar
                :name="name"
                :src="src"
                :initials="initials"
                :hue="hue"
                :tone="tone"
                :shape="shape"
                :status="status"
                :size="40"
                decorative
              />
            </slot>
          </span>
          <span class="nx-entity-card__meta">
            <span :id="nameId" class="nx-entity-card__name">{{ name }}</span>
            <span v-if="subtitle" class="nx-entity-card__subtitle">{{ subtitle }}</span>
            <span v-if="badge" class="nx-entity-card__badge">{{ badge }}</span>
          </span>
        </div>
        <dl v-if="details.length" class="nx-entity-card__details">
          <div v-for="(line, index) in details" :key="index" class="nx-entity-card__line">
            <dt v-if="line.label">{{ line.label }}</dt>
            <dd>{{ line.value }}</dd>
          </div>
        </dl>
        <div v-if="slots.card" class="nx-entity-card__extra">
          <slot name="card" />
        </div>
        <div v-if="slots.actions" class="nx-entity-card__actions">
          <slot name="actions" :close="close" />
        </div>
      </div>
    </NxPopover>
  </span>
</template>

<style scoped>
.nx-entity-chip {
  display: inline-flex;
  max-width: 100%;
  vertical-align: middle;
}
.nx-entity-chip.is-empty {
  color: var(--nxt-fg-muted);
}
.nx-entity-chip :deep(.nx-popover-anchor) {
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
}

.nx-entity-chip__trigger {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--nxt-space-xs) * 0.75);
  max-width: 100%;
  min-width: 0;
  margin: 0;
  padding: calc(var(--nxt-space-2xs) / 2);
  padding-inline-end: calc(var(--nxt-space-xs) * 0.75);
  border: 0;
  border-radius: var(--nxt-radius-full);
  background: transparent;
  color: inherit;
  font: inherit;
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-entity-chip__trigger:hover,
.nx-entity-chip.is-open .nx-entity-chip__trigger {
  background: var(--nxt-surface-sunken-bg);
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-border-subtle);
}
.nx-entity-chip__trigger:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
.nx-entity-chip__media {
  flex: none;
  display: inline-grid;
  place-items: center;
}
.nx-entity-chip__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-entity-chip__trigger.is-link .nx-entity-chip__name {
  color: var(--nxt-brand-subtle-fg);
}
.nx-entity-chip__trigger.is-link:hover .nx-entity-chip__name {
  text-decoration: underline;
  text-underline-offset: calc(var(--nxt-size-unit) * 0.125);
}

.nx-entity-chip :deep(.nx-popover-panel) {
  padding: 0;
  border-radius: var(--nxt-radius-lg);
  overflow: hidden;
}
.nx-entity-card {
  display: flex;
  flex-direction: column;
  min-width: var(--nxt-panel-width-md);
  max-width: min(var(--nxt-panel-width-xl), 90vw);
  outline: none;
}
.nx-entity-card__head {
  display: flex;
  align-items: flex-start;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--nxt-brand-bg) 7%, transparent) 0%,
    transparent 100%
  );
}
.nx-entity-card__media {
  flex: none;
  display: inline-grid;
  place-items: center;
}
.nx-entity-card__meta {
  display: flex;
  flex-direction: column;
  gap: calc(var(--nxt-space-2xs) / 2);
  min-width: 0;
  padding-block-start: calc(var(--nxt-space-2xs) / 2);
}
.nx-entity-card__name {
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-fg);
  overflow-wrap: anywhere;
}
.nx-entity-card__subtitle {
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
  overflow-wrap: anywhere;
}
.nx-entity-card__badge {
  align-self: flex-start;
  margin-block-start: var(--nxt-space-2xs);
  padding: calc(var(--nxt-space-2xs) / 2) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-sunken-bg);
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-border);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-subtle);
}
.nx-entity-card__details {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: var(--nxt-space-sm);
  row-gap: var(--nxt-space-2xs);
  margin: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-md) var(--nxt-space-sm);
  font-size: var(--nxt-font-size-sm);
}
.nx-entity-card__line {
  display: contents;
}
.nx-entity-card__line dt {
  color: var(--nxt-fg-muted);
}
.nx-entity-card__line dd {
  grid-column: 2;
  margin: 0;
  color: var(--nxt-fg);
  overflow-wrap: anywhere;
}
.nx-entity-card__extra {
  padding: 0 var(--nxt-space-md) var(--nxt-space-sm);
}
.nx-entity-card__actions {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-hairline);
  padding: var(--nxt-space-2xs);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
@media (prefers-reduced-motion: reduce) {
  .nx-entity-chip__trigger {
    transition: none;
  }
}
</style>
